import { registerSW } from 'virtual:pwa-register';
import { checkForUpdate, setUpdateApplier } from '../shared/versionCheck';

// Proactively ask the service worker to look for a new bundle on this cadence,
// so an idle tab still notices a deploy.
const SW_UPDATE_POLL_MS = 30 * 60 * 1000;
// How long to wait for a newly found worker to finish installing.
const INSTALL_WAIT_MS = 10_000;
// How long to wait for that worker to take control before reloading anyway.
const HANDOVER_WAIT_MS = 5_000;

let swRegistration: ServiceWorkerRegistration | null = null;

/**
 * Registers the Workbox-generated service worker and wires up "apply update".
 *
 * The plugin runs in 'prompt' mode (see vite.config.ts): a new bundle installs
 * and then WAITS instead of activating itself, which is what lets us show the
 * versioned toast and let the user choose when to reload.
 *
 * Applying the update is done explicitly here rather than through the plugin's
 * `updateSW()` helper, because what that helper does depends on `registerType`
 * (in 'autoUpdate' it is a no-op — the bug this replaced). Doing it directly
 * keeps the behaviour independent of that setting.
 */
export function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return;

  registerSW({
    immediate: true,
    onNeedRefresh() {
      // A new bundle is waiting — confirm which version and prompt.
      void checkForUpdate(false);
    },
    onOfflineReady() {
      // Optional: surface that the app is now fully cached for offline use.
    },
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return;
      swRegistration = registration;
      window.setInterval(() => void registration.update(), SW_UPDATE_POLL_MS);
    },
  });

  setUpdateApplier(applyUpdate);
}

/**
 * Reload onto the newly deployed bundle.
 *
 * Two things make this more than a location.reload():
 *
 *  1. The worker serves navigations from its precached index.html, so reloading
 *     while the OLD worker still controls the page just re-serves the OLD
 *     bundle. The new worker has to take control first — which needs
 *     `clientsClaim: true` in the workbox config, because skipWaiting only
 *     ACTIVATES a worker and an activated worker still does not control pages
 *     that were already open. Without the claim this whole dance ends in
 *     "close and re-open the app", which is the one outcome it exists to avoid.
 *  2. `registration.update()` resolves as soon as the check is done — often
 *     while the new worker is still installing. Messaging it to skip waiting at
 *     that point reaches nothing, so we wait for it to reach `waiting` first.
 *
 * IT NO LONGER DEPENDS ON THE HANDOVER TAKING.
 *
 * The clean path is skipWaiting → clients.claim() → controllerchange → reload, and it failed on real
 * machines three times on 2026-08-19: first because sw.js was missing from the deployment, then
 * because the worker activated without claiming the already-open page, and then again with both of
 * those fixed, clientsClaim confirmed present in the deployed worker and every precache entry
 * verified reachable from outside. At that point the honest conclusion was that this handshake
 * cannot be relied on from where I can see.
 *
 * So if the controller does not change, every registration is unregistered and the page reloads.
 * With nothing controlling it, the reload is served from the network and the new bundle wins; the
 * next load registers the new worker from scratch. That is exactly what closing and reopening the
 * app achieves — done for the user rather than asked of them, which was the entire point of the
 * prompt. It costs one uncached load, and whoever is looking at the dialog is online by definition,
 * because the prompt came from fetching /version.txt moments earlier.
 *
 * Reporting is the last resort now, not the fallback: versionCheck says "close and re-open" only
 * when this has already been tried for this version and the build still has not moved.
 */
async function applyUpdate(): Promise<void> {
  try {
    const registration =
      swRegistration ?? (await navigator.serviceWorker.getRegistration()) ?? null;

    if (registration) {
      const controlled = navigator.serviceWorker.controller !== null;

      if (!registration.waiting) {
        // Our /version.txt check can notice a deploy before the worker does.
        await registration.update();
        await waitForWaitingWorker(registration, INSTALL_WAIT_MS);
      }

      if (registration.waiting) {
        const tookControl = controllerChanged(HANDOVER_WAIT_MS);
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        if (await tookControl) {
          // The new worker owns the page, so a plain reload serves the new bundle.
          window.location.reload();
          return;
        }
      }

      // Either there was nothing to hand over to, or the hand-off did not take in time. Both leave
      // the old worker answering every navigation, so take it out of the way instead of reloading
      // into the same bundle again. Only meaningful when something is actually in control.
      if (controlled) {
        await unregisterAll();
      }
    }
  } catch {
    // Offline, or the registration went away — fall through and reload anyway.
  }

  window.location.reload();
}

/**
 * Remove every service worker registration for this origin, AND EMPTY ITS CACHES.
 *
 * The escape hatch that makes the update unconditional. Unregistering alone is not enough:
 * unregistration is deferred until the page it controls goes away, so the reload immediately
 * afterwards is still served by the outgoing worker out of its precache — the same old bundle, and
 * the version appears not to have moved. Deleting the caches leaves it nothing to serve, so it
 * falls through to the network on that first reload rather than the one after.
 *
 * Offline capability is gone until the new worker installs on the next load. That is a fair trade
 * for an update that actually applies, and whoever is looking at the prompt is online by
 * definition — it came from fetching /version.txt moments earlier.
 */
async function unregisterAll(): Promise<void> {
  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((r) => r.unregister()));
  } catch {
    // Fall through: the caches are the half that makes the reload work anyway.
  }

  try {
    const keys = await caches.keys();
    await Promise.all(keys.map((key) => caches.delete(key)));
  } catch {
    // Storage denied. The reload still happens; at worst it serves the old bundle once more, and
    // the next check escalates rather than looping.
  }
}

/**
 * Resolves once a worker is sitting in `waiting` (installed and ready to take
 * over), or on timeout. Returns whatever is waiting, which may be null if the
 * server had nothing newer.
 */
function waitForWaitingWorker(
  registration: ServiceWorkerRegistration,
  timeoutMs: number,
): Promise<ServiceWorker | null> {
  if (registration.waiting) return Promise.resolve(registration.waiting);

  return new Promise((resolve) => {
    const watched = new Set<ServiceWorker>();
    const timer = window.setTimeout(finish, timeoutMs);

    function finish() {
      window.clearTimeout(timer);
      registration.removeEventListener('updatefound', onUpdateFound);
      watched.forEach((worker) => worker.removeEventListener('statechange', onStateChange));
      resolve(registration.waiting);
    }
    function onStateChange() {
      if (registration.waiting) finish();
    }
    function watch(worker: ServiceWorker | null) {
      if (!worker || watched.has(worker)) return;
      watched.add(worker);
      worker.addEventListener('statechange', onStateChange);
    }
    function onUpdateFound() {
      watch(registration.installing);
    }

    registration.addEventListener('updatefound', onUpdateFound);
    watch(registration.installing);
  });
}

/**
 * Resolves TRUE when a new worker takes control of this page, FALSE on timeout.
 *
 * The boolean is the whole point. It used to resolve void, so a hand-off that never happened was
 * indistinguishable from one that did and the code reloaded either way — straight back into the
 * bundle it was trying to leave.
 */
function controllerChanged(timeoutMs: number): Promise<boolean> {
  return new Promise((resolve) => {
    const timer = window.setTimeout(() => finish(false), timeoutMs);
    function finish(changed: boolean) {
      window.clearTimeout(timer);
      navigator.serviceWorker.removeEventListener('controllerchange', onChange);
      resolve(changed);
    }
    function onChange() {
      finish(true);
    }
    navigator.serviceWorker.addEventListener('controllerchange', onChange, { once: true });
  });
}
